import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { Box, List, ListItem, ListItemText, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { useTheme } from "@mui/material/styles";
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import TabsField from "components/Tab";
import MainCard from "components/cards/MainCard";
import Banner from "components/Banner";
import GlobalAlert from "components/Notification";
import Loader from "components/Loader";
import { SolidButton } from "components/buttons/SolidButton";
import CustomMenu from "components/Menu";
import useZustandStore from "zustandStore";
import breadcrumbStyle from "components/@extended/mainStyle.module.scss";
import { getSummaryReportRangeOptions } from "./helper";
import { v4 as uuidv4 } from "uuid";
import Overview from "./Overview";
import Topology from "./Topology";
import Devices from "./Devices";
import Clients from "./Clients";
import WirelessInsight from "./WirelessInsight";
import WiredInsight from "./WiredInsight";
import AInsight from "./AInsight";
import Logs from "./Logs";

import { ReactComponent as ChevronUp } from "assets/images/icons/icn_pagination_chevron_up_light.svg";
import { ReactComponent as ChevronDown } from "assets/images/icons/icn_pagination_chevron_down_light.svg";
import { ReactComponent as ExportIcon } from "assets/images/icons/icn_btn_export.svg";

const tabData = [
  { name: "Overview", value: "overview" },
  { name: "Topology", value: "topology" },
  { name: "Devices", value: "devices" },
  { name: "Clients", value: "clients" },
  { name: "Wireless Insight", value: "wireless-insight" },
  { name: "Wired Insight", value: "wired-insight" },
  { name: "Health AI Insight", value: "ainsight" },
  { name: "Logs", value: "logs" },
];

const exportOptions = [
  { label: "Export to PDF", value: "pdf" },
  { label: "Export to HTML", value: "html" },
];

const TimeRangeSelect = ({ selectedRange, onChange, options }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);
  const selectedOption = options.find(
    (option) => option.value === selectedRange?.value,
  );

  const handleClose = () => setAnchorEl(null);

  return (
    <Box>
      <Box
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          display: "flex",
          alignItems: "center",
          width: "340px",
          height: "32px",
          border: "1px solid #707ca8",
          borderRadius: "8px",
          bgcolor: "#fff",
          cursor: "pointer",
          overflow: "hidden",
        }}
      >
        <Typography
          sx={{
            flexGrow: 1,
            px: 1.25,
            fontSize: theme.typography.customFontSize[2],
            color: "primary.main",
            fontWeight: theme.typography.customFontWeight[3],
            whiteSpace: "nowrap",
          }}
        >
          {selectedOption?.label}
        </Typography>
        <Box
          sx={{
            width: "32px",
            height: "100%",
            bgcolor: "#172264",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isOpen ? <ChevronUp /> : <ChevronDown />}
        </Box>
      </Box>
      <CustomMenu
        anchorEl={anchorEl}
        handleMenuClose={handleClose}
        maxWidth="340px"
        minWidth="340px"
      >
        <List
          className={breadcrumbStyle["custom-scrollbar"]}
          sx={{
            whiteSpace: "nowrap",
            pt: "5px",
            pl: "10px",
            pr: "10px",
            mr: "10px",
            maxHeight: "180px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {options.map((option) => (
            <ListItem
              key={option.value}
              button
              selected={selectedRange?.value === option.value}
              onClick={() => {
                onChange(option);
                handleClose();
              }}
              sx={{
                color: "primary.main",
                px: "10px",
                my: "5px",
                borderRadius: "5px",
                minHeight: "32px",
                "&:hover": { backgroundColor: "#e8ebfc" },
                "&.Mui-selected": { backgroundColor: "#dbe0fc" },
              }}
            >
              <ListItemText
                primary={option.label}
                primaryTypographyProps={{
                  sx: {
                    fontSize: theme.typography.customFontSize[2],
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </ListItem>
          ))}
        </List>
      </CustomMenu>
    </Box>
  );
};

const SummaryReport = () => {
  const { scopeReady } = useOutletContext();
  const theme = useTheme();
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const serviceStatus = useZustandStore((state) => state.serviceStatus);
  const selectedSite = auth.sites;
  const [summaryReportUuid] = useState(uuidv4());
  const [weeklyRanges, setWeeklyRanges] = useState([]);
  const [reportsLoaded, setReportsLoaded] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(null);
  const [tabVal, setTabVal] = useState("overview");
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    if (!isAuthReady || !scopeReady || !serviceStatus) return;

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports.read";
    const restfulPlaceholders = standalone
      ? { orgId: auth.realm?.id, siteId: auth.sites?.id }
      : { entId: auth.realm?.id, orgId: auth.orgs?.id, siteId: auth.sites?.id };

    const loadReports = async () => {
      const { getSendBatchData } = await sendBatch([{ apiKey, requestUuid: summaryReportUuid, restfulPlaceholders, params: {} }]);
      const items = getSendBatchData({ requestUuid: summaryReportUuid })?.data?.items || [];
      const options = getSummaryReportRangeOptions(items);
      setWeeklyRanges(options);
      setSelectedTimeRange((currentRange) => {
        if (!currentRange?.summaryReportId) return options[0] || null;
        return (
          options.find(
            (option) =>
              option.summaryReportId === currentRange.summaryReportId,
          ) || null
        );
      });
      setReportsLoaded(true);
    };

    loadReports();
  }, [
    auth,
    isAuthReady,
    scopeReady,
    sendBatch,
    serviceStatus,
    standalone,
    summaryReportUuid,
  ]);

  if (!scopeReady || !serviceStatus) return <Loader />;

  const handleTab = (_event, newValue) => setTabVal(newValue);
  const handleExportMenuClose = () => setExportMenuAnchorEl(null);

  const handleExportOptionClick = async (format) => {
    handleExportMenuClose();
    if (!selectedTimeRange?.summaryReportId || isExporting) return;

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/exports.create"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/exports.create";
    const restfulPlaceholders = standalone
      ? {
        orgId: auth.realm?.id,
        siteId: auth.sites?.id,
        summaryReportId: selectedTimeRange.summaryReportId,
      }
      : {
        entId: auth.realm?.id,
        orgId: auth.orgs?.id,
        siteId: auth.sites?.id,
        summaryReportId: selectedTimeRange.summaryReportId,
      };

    setIsExporting(true);
    setExportError("");

    try {
      const requestUuid = uuidv4();
      const { getSendBatchData } = await sendBatch([
        {
          apiKey,
          requestUuid,
          restfulPlaceholders,
          params: { format },
        },
      ]);
      const response = getSendBatchData({ requestUuid });
      const downloadUrl = response?.data?.downloadUrl;

      if (!downloadUrl) {
        throw new Error(response?.message || "The export could not be generated.");
      }

      const downloadLink = document.createElement("a");
      downloadLink.href = downloadUrl;
      downloadLink.download = response.data.fileName || "";
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (error) {
      setExportError(error?.message || "The export could not be generated. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const renderTabContent = () => {
    const tabProps = {
      auth,
      selectedSite,
      timeRange: selectedTimeRange,
      licenseStatus: serviceStatus,
    };

    switch (tabVal) {
      case "topology":
        return <Topology {...tabProps} />;
      case "devices":
        return <Devices {...tabProps} />;
      case "clients":
        return <Clients {...tabProps} />;
      case "wireless-insight":
        return <WirelessInsight {...tabProps} />;
      case "wired-insight":
        return <WiredInsight {...tabProps} />;
      case "ainsight":
        return <AInsight {...tabProps} />;
      case "logs":
        return <Logs {...tabProps} />;
      case "overview":
      default:
        return <Overview {...tabProps} />;
    }
  };

  return (
    <MainCard>
      {serviceStatus?.services?.unityPro === false && (
        <Box sx={{ m: "20px 20px 0" }}>
          <Banner
            color='yellow'
            text='Summary Report is a Nuclias Unity Pro feature. Please subscribe to Nuclias Unity Pro to view this report.'
          />
        </Box>
      )}
      <Box sx={{ m: "20px 20px 0" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.25 }}>
          <Typography
            sx={{
              color: "#172664",
              fontFamily: theme.typography.fontFamily,
              fontWeight: "bold",
            }}
          >
            Time Range
          </Typography>
          <TimeRangeSelect
            selectedRange={selectedTimeRange}
            onChange={setSelectedTimeRange}
            options={weeklyRanges}
          />
        </Box>
      </Box>
      <GlobalAlert
        open={Boolean(exportError)}
        severity="error"
        message={exportError}
        onClose={() => setExportError("")}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          m: "20px",
        }}
      >
        <TabsField
          tabsStyles={{
            flex: 1,
            minWidth: 0,
            "& .MuiTab-root": {
              minWidth: 0,
              px: 1.5,
              whiteSpace: "nowrap",
            },
          }}
          tabVal={tabVal}
          handleTabsChange={handleTab}
          tabData={tabData}
        />
        <Box sx={{ flexShrink: 0 }}>
          <SolidButton
            aria-controls={exportMenuAnchorEl ? "summary-report-export-menu" : undefined}
            aria-expanded={exportMenuAnchorEl ? "true" : undefined}
            aria-haspopup="menu"
            iconStart={<ExportIcon />}
            width="128px"
            height="32px"
            sx={{ whiteSpace: "nowrap" }}
            disabled={!selectedTimeRange?.summaryReportId || isExporting}
            onClick={(event) => setExportMenuAnchorEl(event.currentTarget)}
          >
            {isExporting ? "Exporting..." : "Export Report"}
          </SolidButton>
          <CustomMenu
            anchorEl={exportMenuAnchorEl}
            handleMenuClose={handleExportMenuClose}
            maxWidth="160px"
            minWidth="160px"
            mt={1}
          >
            <List
              id="summary-report-export-menu"
              disablePadding
              sx={{
                boxSizing: "content-box",
                width: "140px",
                p: "5px 10px",
              }}
            >
              {exportOptions.map((option) => (
                <ListItem
                  button
                  key={option.value}
                  onClick={() => handleExportOptionClick(option.value)}
                  sx={{
                    minHeight: "32px",
                    px: "10px",
                    my: "5px",
                    gap: 1,
                    borderRadius: "5px",
                    color: "primary.main",
                    "&:hover": { backgroundColor: "#e8ebfc" },
                  }}
                >
                  <FileDownloadOutlinedIcon color="primary" sx={{ fontSize: 16 }} />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      sx: {
                        fontSize: theme.typography.customFontSize[2],
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </CustomMenu>
        </Box>
      </Box>
      {reportsLoaded && weeklyRanges.length === 0 ? (
        <Box sx={{ minHeight: "240px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
            No data available
          </Typography>
        </Box>
      ) : selectedTimeRange && renderTabContent()}
    </MainCard>
  );
};

export default SummaryReport;
